import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { resolveImageUrl } from '../lib/imageUrl';
import { Building, Megaphone, Printer, Package, CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  building: Building,
  megaphone: Megaphone,
  printer: Printer,
  package: Package,
};

export default function ServicesPage() {
  const { t, getLocalizedContent } = useLanguage();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  // Detailed service features
  const serviceFeatures = {
    building: [
      'Construction de bâtiments résidentiels et commerciaux',
      'Travaux de génie civil et infrastructures',
      'Rénovation et réhabilitation',
      'Routes et voiries',
      'Terrassement et VRD',
      'Supervision de chantiers'
    ],
    megaphone: [
      'Développement web et création de sites internet',
      'Marketing digital et campagnes publicitaires',
      'Gestion des réseaux sociaux',
      'Stratégie de communication digitale',
      'Branding et identité visuelle',
      'SEO / Référencement naturel',
      'Création de contenu multimédia',
      'Relations publiques et événementiel'
    ],
    printer: [
      'Cartes de visite et papeterie',
      'Flyers, brochures et catalogues',
      'Affiches et banderoles',
      'Kakémonos et roll-ups',
      'Tee-shirts et objets publicitaires',
      'Signalétique intérieure et extérieure'
    ],
    package: [
      'Équipements informatiques',
      'Matériel de bureau',
      'Fournitures industrielles',
      'Équipements de protection (EPI)',
      'Consommables et accessoires',
      'Service après-vente'
    ]
  };

  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <span className="inline-block px-4 py-1 bg-[#0B6AB3]/20 text-[#0B6AB3] text-sm font-medium rounded-full mb-4">
              Nos expertises
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-white font-['Montserrat']">
              {t('services_title')}
            </h1>
            <p className="text-gray-300 mt-4 max-w-2xl mx-auto text-lg">
              {t('services_subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Building;
              const features = serviceFeatures[service.icon] || [];
              const isEven = index % 2 === 0;

              return (
                <motion.div
                  key={service.id}
                  id={service.id}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  className={`grid lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}
                  data-testid={`service-detail-${index}`}
                >
                  <div className={isEven ? '' : 'lg:order-2'}>
                    <div className="w-14 h-14 bg-[#0B6AB3]/10 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="h-7 w-7 text-[#0B6AB3]" />
                    </div>
                    <h2 className="text-3xl font-bold text-[#0F172A] font-['Montserrat']">
                      {getLocalizedContent(service.title)}
                    </h2>
                    <p className="text-gray-600 mt-4 leading-relaxed">
                      {getLocalizedContent(service.description)}
                    </p>
                    
                    <div className="mt-6 space-y-3">
                      {features.map((feature, idx) => (
                        <div key={idx} className="flex items-start space-x-3">
                          <CheckCircle className="h-5 w-5 text-[#0B6AB3] flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Link to="/contact">
                      <Button className="mt-8 bg-[#0B6AB3] hover:bg-[#095c9e] text-white">
                        Demander un devis
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                  <div className={isEven ? '' : 'lg:order-1'}>
                    <Card className="overflow-hidden border-0 shadow-xl relative">
                      <img
                        src={resolveImageUrl(service.image_url) || 'https://via.placeholder.com/600x400'}
                        alt={getLocalizedContent(service.title)}
                        className="w-full h-80 lg:h-96 object-cover"
                      />
                      {/* Logo GTIC Print & Com pour le service d'impression */}
                      {service.icon === 'printer' && (
                        <div className="absolute bottom-4 right-4 bg-white rounded-lg p-2 shadow-lg">
                          <img 
                            src="https://customer-assets.emergentagent.com/job_corpsite-gtic/artifacts/ryrdb316_GTIC%20STUDIO%20LOGOR.png"
                            alt="GTIC Print & Com"
                            className="h-16 w-auto"
                          />
                        </div>
                      )}
                    </Card>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-[#F8F9FA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-[#0F172A] font-['Montserrat']">
              Un projet en tête ?
            </h2>
            <p className="text-gray-600 mt-4 text-lg max-w-2xl mx-auto">
              Contactez-nous pour discuter de vos besoins. Notre équipe est prête à vous accompagner dans la réalisation de vos projets.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-[#E50E24] hover:bg-[#c40c1f] text-white px-8" data-testid="services-cta">
                  Nous contacter
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/projects">
                <Button size="lg" variant="outline" className="border-[#0B6AB3] text-[#0B6AB3] hover:bg-[#0B6AB3] hover:text-white">
                  Voir nos réalisations
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
